/**
 * Helpers for the coaching-calls calendar-first redesign.
 *
 * Two responsibilities:
 *   1. Occurrence resolution — given a series row + override rows +
 *      a date, decide whether a call exists on that date and what
 *      its effective time/description/duration/status are.
 *   2. Lightweight markdown rendering for the preview side of the
 *      note editor (no external dep — we own the regex).
 */

const ONE_DAY_MS = 86400000;

export const FREQUENCY_OPTIONS = [
  { value: 'one-time', label: 'One-time' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly',  label: 'Monthly' },
];

export const STATUS_OPTIONS = [
  { value: '',          label: 'Upcoming' },
  { value: 'completed', label: 'Completed' },
  { value: 'missed',    label: 'Missed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function parseDateStr(s) {
  return new Date(s + 'T00:00:00');
}

export function fmt12(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

const DOW_LABELS_FULL = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday',
];

const ORDINAL = ['1st', '2nd', '3rd', '4th', '5th', '6th'];

/**
 * Plain-English summary of a series' recurrence — the kind of thing
 * a coach wants to see in the call detail modal.
 *   "Every Wednesday at 6:00 PM"
 *   "Biweekly on Tuesdays at 7:30 PM (starting Apr 9)"
 *   "Monthly on the 15th at 10:00 AM"
 *   "One-time"
 */
export function describeRecurrence(series) {
  if (!series) return '';
  const time = fmt12(series.call_time);
  const dow = DOW_LABELS_FULL[series.day_of_week] ?? '';
  const at = time ? ` at ${time}` : '';
  if (series.frequency === 'one-time') {
    return `One-time${at}`;
  }
  if (series.frequency === 'weekly') {
    return `Every ${dow}${at}`;
  }
  if (series.frequency === 'biweekly') {
    const ref = series.biweekly_ref_date
      ? parseDateStr(series.biweekly_ref_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : null;
    return `Biweekly on ${dow}s${at}${ref ? ` (starting ${ref})` : ''}`;
  }
  if (series.frequency === 'monthly') {
    const ref = series.biweekly_ref_date ? parseDateStr(series.biweekly_ref_date) : null;
    if (ref) {
      const day = ref.getDate();
      const ord = ORDINAL[Math.floor((day - 1) / 7)] ?? `${day}th`;
      return `Monthly on the ${ord} ${dow}${at}`;
    }
    return `Monthly${at}`;
  }
  return '';
}

/**
 * Does the recurrence pattern of `series` naturally land on `dateStr`?
 * Doesn't account for overrides — caller layers those on top.
 */
function matchesPattern(series, dateStr) {
  const date = parseDateStr(dateStr);
  if (date.getDay() !== series.day_of_week && series.frequency !== 'monthly') {
    return false;
  }
  const refStr = series.biweekly_ref_date;
  const ref = refStr ? parseDateStr(refStr) : null;

  if (series.frequency === 'one-time') {
    return refStr ? dateStr === refStr : false;
  }

  if (series.frequency === 'weekly') {
    if (ref && date < ref) return false;
    return true;
  }

  if (series.frequency === 'biweekly') {
    if (!ref) return false;
    if (date < ref) return false;
    const diffWeeks = Math.round((date - ref) / (ONE_DAY_MS * 7));
    return diffWeeks % 2 === 0;
  }

  if (series.frequency === 'monthly') {
    if (!ref) return false;
    if (date < ref) return false;
    // "Monthly on the same day-of-month as the ref date." Falls back
    // to the last day of the month when the target month is shorter
    // (e.g. ref on Jan 31 → Feb 28).
    const targetDay = ref.getDate();
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const wantDay = Math.min(targetDay, lastDay);
    return date.getDate() === wantDay;
  }

  return false;
}

/**
 * Resolve whether `series` produces an occurrence on `dateStr`,
 * applying any matching override rows. Returns null when there's
 * no occurrence, or an enriched object with the effective time /
 * description / duration / status / override metadata.
 */
export function resolveOccurrence(series, dateStr, overrides) {
  if (!series || !dateStr) return null;
  // Series cap (set when the coach split the series via "this and
  // future"). Past the cap, the series doesn't produce occurrences.
  if (series.series_ends_at && dateStr > series.series_ends_at) {
    return null;
  }

  const seriesOverrides = (overrides ?? []).filter(o => o.schedule_id === series.id);

  // 1. Cancelled-only override → suppressed occurrence (still
  //    surfaceable as a "Cancelled" entry so the coach sees what
  //    happened to the historical slot).
  const cancelHere = seriesOverrides.find(
    o => o.original_date === dateStr && o.cancelled
  );
  if (cancelHere) {
    return makeOccurrence(series, dateStr, {
      cancelled: true,
      override: cancelHere,
      status: cancelHere.status || 'cancelled',
    });
  }

  // 2. Moved AWAY from this date → no occurrence here today.
  const movedAway = seriesOverrides.find(
    o => o.original_date === dateStr && !o.cancelled && o.new_date && o.new_date !== dateStr
  );
  if (movedAway) return null;

  // 3. Moved TO this date.
  const movedHere = seriesOverrides.find(
    o => o.new_date === dateStr && !o.cancelled
  );
  if (movedHere) {
    return makeOccurrence(series, dateStr, {
      override: movedHere,
      time: movedHere.new_time,
      description: movedHere.description,
      duration: movedHere.duration_minutes,
      status: movedHere.status,
      originalDate: movedHere.original_date,
    });
  }

  // 4. In-place override (status / description / duration set on
  //    the natural occurrence date).
  const inPlace = seriesOverrides.find(
    o => o.original_date === dateStr && !o.cancelled && (!o.new_date || o.new_date === dateStr)
  );
  if (inPlace) {
    if (!matchesPattern(series, dateStr)) return null;
    return makeOccurrence(series, dateStr, {
      override: inPlace,
      time: inPlace.new_time,
      description: inPlace.description,
      duration: inPlace.duration_minutes,
      status: inPlace.status,
    });
  }

  // 5. Default — natural occurrence from the recurrence pattern.
  if (!matchesPattern(series, dateStr)) return null;
  return makeOccurrence(series, dateStr, {});
}

function makeOccurrence(series, dateStr, opts) {
  const time = opts.time || series.call_time;
  const description = opts.description ?? series.description ?? null;
  const duration = opts.duration ?? series.duration_minutes ?? 30;
  return {
    seriesId: series.id,
    athleteId: series.athlete_id,
    date: dateStr,
    time,
    description,
    duration,
    status: opts.status ?? null,
    cancelled: !!opts.cancelled,
    override: opts.override ?? null,
    overrideId: opts.override?.id ?? null,
    originalDate: opts.originalDate ?? dateStr,
    series,
  };
}

/**
 * Walk every series + override and return the resolved list of
 * occurrences within `[startStr, endStr]`. Used to populate the
 * calendar.
 */
export function resolveRange(seriesList, overrides, startStr, endStr) {
  const out = [];
  const start = parseDateStr(startStr);
  const end = parseDateStr(endStr);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dateStr = toDateStr(d);
    for (const s of seriesList) {
      const occ = resolveOccurrence(s, dateStr, overrides);
      if (occ) out.push(occ);
    }
  }
  return out;
}

/**
 * Implicit status when no override has set one explicitly. Past
 * dates default to 'completed' (so coaches see them as historical
 * regardless of whether they noted them); future dates are
 * 'upcoming'.
 */
export function effectiveStatus(occurrence, todayStr) {
  if (occurrence.status) return occurrence.status;
  if (occurrence.cancelled) return 'cancelled';
  if (occurrence.date < todayStr) return 'completed';
  if (occurrence.date === todayStr) return 'today';
  return 'upcoming';
}

export function statusLabel(status) {
  const map = {
    upcoming:  'Upcoming',
    today:     'Today',
    completed: 'Completed',
    missed:    'Missed',
    cancelled: 'Cancelled',
  };
  return map[status] ?? 'Upcoming';
}

export function statusColor(status) {
  // Brand-aligned: navy for upcoming, gold for today, green for
  // completed, gray for missed/cancelled. Returns hex pairs the
  // calendar pills + status chips read directly.
  switch (status) {
    case 'today':     return { bg: '#FCC625', fg: '#00182C', border: '#FCC625' };
    case 'completed': return { bg: '#dcfce7', fg: '#166534', border: '#bbf7d0' };
    case 'missed':    return { bg: '#fef2f2', fg: '#991b1b', border: '#fecaca' };
    case 'cancelled': return { bg: '#f3f4f6', fg: '#6b7280', border: '#e5e7eb' };
    case 'upcoming':
    default:          return { bg: '#eff6ff', fg: '#1e3a8a', border: '#bfdbfe' };
  }
}

/* ─────────────────────────────────────────────────────────────────
   Markdown renderer
   ─────────────────────────────────────────────────────────────────
   Coaches type markdown source in the editor; we render it for the
   read-only preview. Intentionally narrow scope: bold, italic, the
   <u> tag, headings, ordered + unordered lists, links. No tables,
   no code blocks — those aren't useful here and pull in complexity.
*/

function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function inlineFormat(line) {
  // Order matters — process longer tokens first so **bold** doesn't
  // get half-eaten by the *italic* pass.
  let out = escapeHtml(line);
  // Re-allow the small subset of HTML we accept: <u>...</u>.
  out = out.replace(/&lt;u&gt;([\s\S]*?)&lt;\/u&gt;/g, '<u>$1</u>');
  // [text](url)
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_, text, url) => `<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`
  );
  // **bold**
  out = out.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  // *italic* (avoid double-counting bold's leftover asterisks via
  // the bold pass running first)
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  return out;
}

/**
 * Render markdown source to a small HTML string. Used by the
 * preview pane in the note editor and by the read-only display
 * shown when the call detail modal first opens.
 */
export function renderMarkdown(src) {
  if (!src) return '';
  const lines = src.split('\n');
  const out = [];
  let listType = null; // 'ul' | 'ol' | null

  function closeList() {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  }

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (!line) {
      closeList();
      out.push('');
      continue;
    }
    // Headings
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      closeList();
      const level = h[1].length + 2; // ## → h4, ### → h5; we use h2/h3/h4 for visual hierarchy
      const tag = level <= 4 ? `h${Math.max(2, h[1].length + 1)}` : 'h4';
      out.push(`<${tag}>${inlineFormat(h[2])}</${tag}>`);
      continue;
    }
    // Unordered list
    const ul = line.match(/^[-*]\s+(.*)$/);
    if (ul) {
      if (listType !== 'ul') { closeList(); out.push('<ul>'); listType = 'ul'; }
      out.push(`<li>${inlineFormat(ul[1])}</li>`);
      continue;
    }
    // Ordered list
    const ol = line.match(/^\d+\.\s+(.*)$/);
    if (ol) {
      if (listType !== 'ol') { closeList(); out.push('<ol>'); listType = 'ol'; }
      out.push(`<li>${inlineFormat(ol[1])}</li>`);
      continue;
    }
    closeList();
    out.push(`<p>${inlineFormat(line)}</p>`);
  }
  closeList();
  return out.filter(Boolean).join('');
}

/**
 * Default scaffold for new notes (HTML). Coaches can hit "Insert
 * template" to drop these section headers in.
 */
export const NOTE_TEMPLATE_HTML =
  '<h3>Agenda</h3><p><br></p>' +
  '<h3>Athlete updates</h3><p><br></p>' +
  '<h3>Training discussion</h3><p><br></p>' +
  '<h3>Action items</h3><ul><li><br></li></ul>' +
  '<h3>Coach notes</h3><p><br></p>' +
  '<h3>Follow-up</h3><ul><li><br></li></ul>';

/**
 * Treat a stored note as HTML. If it has no tags, assume it's
 * legacy markdown / plain text and render it through the markdown
 * helper so old notes don't display as raw `**text**`.
 */
export function noteToHtml(notes) {
  if (!notes) return '';
  if (/<[a-z][\s\S]*?>/i.test(notes)) return notes;
  return renderMarkdown(notes);
}
